/**
 * Mode configuration. Each mode declares its phase, a one-line description, the
 * default layers it boots with (design plan section 7), and which manual-plot
 * tools belong to it. Keeping this as data, not scattered through components,
 * means the mode model is read in one place and a later phase can extend it
 * without hunting through JSX.
 */
import type { Mode } from '@/store/shell';
import type { ActiveTool } from '@/store';

export type Phase = 'P1' | 'P2' | 'P3';

export interface ModeConfig {
  name: Mode;
  /** Earliest phase the mode is usable in. */
  phase: Phase;
  /** One line for the mode sheet. */
  blurb: string;
  /**
   * Gated behind sign-in. Training only — it should not clutter navigation for
   * someone who just wants a chart, so it stays locked until a course is active.
   */
  gated?: boolean;
  /**
   * Absent from the phase 1 interface as a working mode. Live needs live
   * sensors, so it is a preview only for now.
   */
  preview?: boolean;
}

export const MODES: ModeConfig[] = [
  {
    name: 'Live',
    phase: 'P3',
    blurb: 'Own ship live from the GNSS feed',
  },
  {
    name: 'Plotting',
    phase: 'P1',
    blurb: 'Plan and plot: tides, routes, fixes, pilotage',
  },
  {
    name: 'Training',
    phase: 'P2',
    blurb: 'Signed in, course active',
    gated: true,
  },
];

/**
 * A manual-plot tool in the plotting palette. The id maps straight onto the
 * existing ActiveTool union so picking one drives the same chartwork store the
 * milestone work already built.
 */
export interface PlotTool {
  id: ActiveTool;
  name: string;
  detail: string;
  /** How many chart taps the tool needs once chosen. */
  points: 1 | 2 | 3;
}

/**
 * The single-tap manual-plotting tools plus the fix builder. DR, EP, VRM and
 * EBL are armed as single-tap tools; the fix is a multi-observation
 * construction, so it opens the fix builder instead. The other multi-step tools
 * (measure, running fix, course to steer) join the palette in a later pass.
 *
 * Outside Training every mode shows its full toolset; the syllabus-level filter
 * that trims this down lives only inside Training (design plan section 5).
 */
export const PLOT_TOOLS: PlotTool[] = [
  { id: 'fix', name: 'Plot fix', detail: 'From observations', points: 3 },
  { id: 'running_fix', name: 'Running fix', detail: 'Bearing, run, bearing', points: 2 },
  { id: 'dr', name: 'DR', detail: 'Dead reckoning', points: 1 },
  { id: 'ep', name: 'EP', detail: 'Estimated position', points: 1 },
  { id: 'vrm', name: 'VRM', detail: 'Variable range', points: 1 },
  { id: 'ebl', name: 'EBL', detail: 'Bearing line', points: 1 },
  { id: 'set_drift', name: 'Set & drift', detail: 'Also CMG / SMG', points: 2 },
  { id: 'cts', name: 'Course to steer', detail: 'Tidal triangle', points: 2 },
];
